import { useEffect, useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import { Alert, FormField, FormLabel, Modal } from "@impero-com/ui";
import moment from "moment-timezone";

import { DatePicker } from "components/datePicker";
import { CommentField } from "components/modals/CommentField";
import { Footer } from "components/modals/Footer";
import { useModalComment } from "components/modals/useModalComment";
import { useCurrentUserTimezone } from "utils/currentTimezone";

import {
    CALENDAR_DAY_FORMAT,
    CalendarDay,
    SelectionDueDates,
    dayInTimezone,
    formatDayLong,
    minSelectableDay,
    summarizeDueDates,
    timezoneAbbreviation,
} from "../utils/extendDueDate";

import * as theme from "./ControlActivityExtendDueDateModal.css";

interface ControlActivityExtendDueDateModalProps {
    open: boolean;
    loading: boolean;
    selectionCount: number;
    /** Final due date bounds of the selection; `null` while nothing is selected. */
    selectionDueDates: SelectionDueDates | null;
    onConfirm: (newDueDay: CalendarDay, comment: string) => void;
    onClose: () => void;
}

export function ControlActivityExtendDueDateModal({
    open,
    loading,
    selectionCount,
    selectionDueDates,
    onConfirm,
    onClose,
}: ControlActivityExtendDueDateModalProps) {
    const { t } = useTranslation();
    const timezoneName = useCurrentUserTimezone();
    const [selectedDay, setSelectedDay] = useState<CalendarDay | null>(null);
    const { comment, setComment, trimmed, isValid, isTooLong } =
        useModalComment({ open });

    // Reset the pick each time the modal opens.
    useEffect(() => {
        if (open) {
            // eslint-disable-next-line react-hooks/set-state-in-effect
            setSelectedDay(null);
        }
    }, [open]);

    if (!selectionDueDates) {
        return null;
    }

    const latestDueDay = dayInTimezone(selectionDueDates.latest, timezoneName);
    const minDay = minSelectableDay(selectionDueDates.latest, timezoneName);
    const summary = summarizeDueDates(selectionDueDates, timezoneName);

    return (
        <Modal
            variant="content"
            open={open}
            footer={
                <Footer
                    onClose={onClose}
                    onConfirm={() => {
                        if (selectedDay) {
                            onConfirm(selectedDay, trimmed);
                        }
                    }}
                    loading={loading}
                    okDisabled={selectedDay === null || !isValid}
                    confirmLabel={t("ControlActivitiesList_ExtendDueDate")}
                />
            }
            onClose={onClose}
            title={t("ControlActivitiesList_ExtendDueDate")}
        >
            <div className={theme.body}>
                <div>
                    <div className={theme.selectionCount}>
                        {t(
                            "ControlActivitiesList_ExtendDueDate_SelectedActivities",
                            { count: selectionCount }
                        )}
                    </div>
                    <div className={theme.currentDueDates}>
                        {summary.type === "single"
                            ? t(
                                  "ControlActivitiesList_ExtendDueDate_CurrentDueDate",
                                  { date: summary.date }
                              )
                            : t(
                                  "ControlActivitiesList_ExtendDueDate_CurrentDueDates",
                                  {
                                      earliest: summary.earliest,
                                      latest: summary.latest,
                                  }
                              )}
                    </div>
                </div>
                <FormField>
                    <FormLabel>
                        {t(
                            "ControlActivitiesList_ExtendDueDate_NewDueDateLabel"
                        )}
                    </FormLabel>
                    <DatePicker
                        className={theme.datePicker}
                        value={
                            selectedDay
                                ? moment(selectedDay, CALENDAR_DAY_FORMAT)
                                : null
                        }
                        onChange={(value) =>
                            setSelectedDay(
                                value ? value.format(CALENDAR_DAY_FORMAT) : null
                            )
                        }
                        // Extending only moves the deadline forward: the latest
                        // current due date and everything before it stay disabled.
                        disabledDate={(current) =>
                            current.format(CALENDAR_DAY_FORMAT) < minDay
                        }
                        placeholder={t(
                            "ControlActivitiesList_ExtendDueDate_SelectDatePlaceholder"
                        )}
                    />
                    <div className={theme.caption}>
                        {t(
                            "ControlActivitiesList_ExtendDueDate_MinDateCaption",
                            { date: formatDayLong(latestDueDay) }
                        )}
                    </div>
                </FormField>
                <CommentField
                    value={comment}
                    onChange={setComment}
                    label={t("ControlActivitiesList_ExtendDueDate_ReasonLabel")}
                    placeholder={t(
                        "ControlActivitiesList_ExtendDueDate_ReasonPlaceholder"
                    )}
                    rows={2}
                    error={
                        isTooLong ? t("ControlResult_Comment_Error") : undefined
                    }
                />
                <Alert
                    type="info"
                    showIcon
                    message={t(
                        "ControlActivitiesList_ExtendDueDate_TimezoneNoteTitle",
                        {
                            timezone: timezoneAbbreviation(
                                selectedDay,
                                timezoneName
                            ),
                        }
                    )}
                    description={t(
                        "ControlActivitiesList_ExtendDueDate_TimezoneNoteBody"
                    )}
                />
            </div>
        </Modal>
    );
}
